import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChartLine } from 'lucide-react';
import { Card, LineChart, Skeleton, SubTabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useRoles } from '@/hooks/useAuth';
import { useSeoTimeline } from '@/hooks/useSeoMetrics';
import { can } from '@/utils/permissions';
import { TIMELINE_METRIC_LABELS, TIMELINE_SOURCE_LABELS } from '@/utils/seo';
import type { TimelineMetric, TimelineSeries } from '@/types/api';

type Range = '90' | '365' | 'all';
const RANGES: { id: Range; label: string }[] = [
  { id: '90', label: '3 мес' },
  { id: '365', label: '12 мес' },
  { id: 'all', label: 'Всё' },
];
const SEARCH_METRICS: readonly TimelineMetric[] = ['clicks', 'impressions', 'position'];
const SOURCE_ORDER: readonly string[] = ['google', 'ahrefs', 'dataforseo', 'openpagerank'];
const ORDER: readonly TimelineMetric[] = ['clicks', 'impressions', 'position', 'domain_rating', 'referring_domains', 'organic_traffic', 'page_rank', 'backlinks'];

const whole = (value: number) => Math.round(value).toLocaleString('ru-RU');
const tenth = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
const FORMAT: Partial<Record<TimelineMetric, (value: number) => string>> = { position: tenth, domain_rating: tenth, page_rank: (value) => value.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) };

/** The first and last points in range, and the change between, for the line above the chart. */
function change(series: readonly TimelineSeries[], format: (value: number) => string, invert: boolean): ReactNode {
  const main = series[0];
  if (!main || main.points.length < 2) return null;
  const first = main.points[0]!.value;
  const last = main.points[main.points.length - 1]!.value;
  const delta = last - first;
  const better = invert ? delta < 0 : delta > 0;
  return (
    <span className="muted text-sm">
      сейчас <strong className="numeric">{format(last)}</strong>
      {delta !== 0 ? (
        <span className={better ? 'tone-healthy' : 'tone-down'}>
          {' '}
          {delta > 0 ? '+' : '−'}
          {format(Math.abs(delta))}
        </span>
      ) : null}{' '}
      за период · {TIMELINE_SOURCE_LABELS[main.source] ?? main.source}
    </span>
  );
}

/**
 * The site's figures over time (ADR-0054): Search Console's days, Ahrefs's
 * months, and every weekly read of the link graph. One figure at a time, a
 * line per source; what is missing says what would fill it.
 */
export function SeoTimelineCard({
  siteId,
  metrics = ORDER,
  title = 'Динамика',
}: {
  readonly siteId: string;
  /** Which figures this card offers: Search Console's, or the link graph's. */
  readonly metrics?: readonly TimelineMetric[];
  readonly title?: string;
}): ReactNode {
  const roles = useRoles();
  const timeline = useSeoTimeline(siteId);
  const [metric, setMetric] = useState<TimelineMetric | null>(null);
  const [range, setRange] = useState<Range>('365');

  const view = timeline.data;
  const available = ORDER.filter((item) => metrics.includes(item) && view?.series.some((series) => series.metric === item && series.points.length > 0));
  const wantsSearch = metrics.some((item) => SEARCH_METRICS.includes(item));
  const wantsLinks = metrics.some((item) => !SEARCH_METRICS.includes(item));
  const active = metric && available.includes(metric) ? metric : (available[0] ?? null);
  const since = range === 'all' ? '' : new Date(Date.now() - Number(range) * 24 * 60 * 60_000).toISOString().slice(0, 10);
  const lines = (view?.series ?? [])
    .filter((series) => series.metric === active)
    .map((series) => ({ ...series, points: series.points.filter((point) => point.date >= since) }))
    .filter((series) => series.points.length > 0)
    // The site's own figures first, then the richest history.
    .sort((a, b) => SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source));
  const format = (active && FORMAT[active]) ?? whole;
  const invert = active === 'position';
  const manage = can(roles, 'secrets.manage');

  const missing: ReactNode[] = [];
  if (view && wantsSearch && !view.google) missing.push(<span key="g">Search Console — {manage ? <Link to="/integrations/google">подключить Google</Link> : 'ключ Google не задан'}</span>);
  if (view && wantsLinks && view.ahrefs === 'off') missing.push(<span key="a">история DR и трафика — {manage ? <Link to="/integrations/ahrefs">подключить Ahrefs</Link> : 'ключ Ahrefs не задан'}</span>);

  return (
    <Card title={title} icon={ChartLine}>
      {timeline.isPending ? (
        <Skeleton height={220} />
      ) : timeline.isError ? (
        <ErrorState error={timeline.error} />
      ) : available.length === 0 ? (
        <div className="stack stack-tight">
          <p className="muted">Истории пока нет: она копится с каждым чтением источников и заполняется сразу после подключения Search Console или Ahrefs.</p>
          {missing.length > 0 ? <p className="muted text-sm">Нужно: {missing.map((item, index) => [index > 0 ? '; ' : '', item])}</p> : null}
        </div>
      ) : (
        <div className="stack">
          <div className="row-between row-wrap">
            <SubTabs
              label="Показатель"
              prefix="timeline-"
              active={active!}
              onChange={setMetric}
              tabs={available.map((item) => ({ id: item, label: TIMELINE_METRIC_LABELS[item] ?? item }))}
            />
            <SubTabs label="Период" prefix="range-" active={range} onChange={setRange} tabs={RANGES} />
          </div>
          {lines.length > 0 ? (
            <>
              {change(lines, format, invert)}
              <LineChart
                label={TIMELINE_METRIC_LABELS[active!] ?? ''}
                series={lines.map((series) => ({ key: `${series.metric}/${series.source}`, label: TIMELINE_SOURCE_LABELS[series.source] ?? series.source, points: series.points }))}
                format={format}
                invert={invert}
              />
            </>
          ) : (
            <p className="muted">За этот период точек нет — выберите период длиннее.</p>
          )}
          {missing.length > 0 ? <p className="muted text-sm">Ещё можно добавить: {missing.map((item, index) => [index > 0 ? '; ' : '', item])}</p> : null}
        </div>
      )}
    </Card>
  );
}
